<template>
  <v-card class="trap-panel" elevation="1" data-testid="trap-panel">
    <v-tabs v-model="activeTab" grow>
      <v-tab data-testid="tab-trap-list">{{ $t('tabs.trap_list') }}</v-tab>
      <v-tab data-testid="tab-trap-ranking">{{ $t('tabs.trap_ranking') }}</v-tab>
      <v-tab data-testid="tab-insect-ranking">{{ $t('tabs.insect_ranking') }}</v-tab>
    </v-tabs>

    <v-tabs-items v-model="activeTab" class="tabs-content">
      <v-tab-item class="pa-3">
        <floor-plan-item-list
          :items="normalizedItems"
          type="trap"
          drag-data-key="trapNo"
          @remove="$emit('remove', $event)"
        />
      </v-tab-item>
      <v-tab-item class="pa-3">
        <floor-plan-item-ranking
          ref="trapRanking"
          :items="normalizedItems"
          type="trap"
          :filter-label="insectFilterLabel"
          :place-name="placeName"
          :floor-plan-name="floorPlanName"
          :selected-month="selectedMonth"
        />
      </v-tab-item>
      <v-tab-item class="pa-3">
        <insect-ranking
          ref="insectRanking"
          :traps="traps"
          :events="events"
          :place-name="placeName"
          :floor-plan-name="floorPlanName"
          :selected-month="selectedMonth"
          :is-exporting="exporting"
        />
      </v-tab-item>
    </v-tabs-items>
    <ranking-export-actions
      v-if="isRankingTab"
      :exporting="exporting"
      :disabled="!hasRankingData"
      @export-image="handleExportImage"
      @export-csv="handleExportCsv"
    />
  </v-card>
</template>

<i18n lang="yaml">
ja:
  tabs:
    trap_list: トラップ一覧
    trap_ranking: トラップランキング
    insect_ranking: 虫ランキング
  export_error: エクスポートに失敗しました

en:
  tabs:
    trap_list: Trap List
    trap_ranking: Trap Ranking
    insect_ranking: Insect Ranking
  export_error: Export failed
</i18n>

<script>
import Notifications from '@/mixins/notifications';
import FloorPlanItemList from '../PlaceFloorPlans/FloorPlanItemList';
import FloorPlanItemRanking from '../PlaceFloorPlans/FloorPlanItemRanking';
import InsectRanking from './InsectRanking';
import RankingExportActions from '../PlaceFloorPlans/RankingExportActions';

export default {
  name: 'TrapPanel',
  mixins: [Notifications],
  components: {
    FloorPlanItemList,
    FloorPlanItemRanking,
    InsectRanking,
    RankingExportActions,
  },
  props: {
    traps: { type: Array, required: true },
    events: { type: Array, required: true },
    placeName: { type: String, default: '' },
    floorPlanName: { type: String, default: '' },
    selectedMonth: { type: String, default: '' },
    insectFilterLabel: { type: String, default: '' },
  },
  data() {
    return {
      activeTab: 0,
      exporting: false,
    };
  },
  computed: {
    normalizedItems() {
      return this.traps.map(trap => ({
        id: trap.trapNo,
        label: trap.trapNo,
        count: trap.count,
        isPlaced: trap.isPlaced,
        isPlacedOnOtherFloorPlan: trap.isPlacedOnOtherFloorPlan,
        placedFloorPlanName: trap.placedFloorPlanName,
      }));
    },
    isRankingTab() {
      return this.activeTab === 1 || this.activeTab === 2;
    },
    placedTrapNos() {
      return new Set(
        this.traps.filter(t => t.isPlaced).map(t => t.trapNo),
      );
    },
    hasRankingData() {
      if (this.activeTab === 1) {
        return this.normalizedItems.some(item => item.isPlaced);
      }
      if (this.activeTab === 2) {
        return this.placedTrapNos.size > 0
          && this.events.some(e => this.placedTrapNos.has(e.trap_no));
      }
      return false;
    },
  },
  methods: {
    async handleExportImage() {
      this.exporting = true;
      try {
        if (this.activeTab === 1) {
          await this.$refs.trapRanking.exportImage();
        } else if (this.activeTab === 2) {
          await this.$refs.insectRanking.exportImage();
        }
      } catch (e) {
        console.error(e); // eslint-disable-line no-console
        this.notifyError('export_error');
      } finally {
        this.exporting = false;
      }
    },
    async handleExportCsv() {
      this.exporting = true;
      try {
        if (this.activeTab === 1) {
          this.$refs.trapRanking.exportCsv();
        } else if (this.activeTab === 2) {
          this.$refs.insectRanking.exportCsv();
        }
      } catch (e) {
        console.error(e); // eslint-disable-line no-console
        this.notifyError('export_error');
      } finally {
        this.exporting = false;
      }
    },
  },
};
</script>

<style scoped>
.trap-panel {
  --toolbar-height: 130px;
  position: sticky;
  top: var(--toolbar-height);
  z-index: 10;
  max-height: calc(100vh - var(--toolbar-height));
  display: flex;
  flex-direction: column;
}

.tabs-content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

</style>
